import type { AddonShotUploadResponse, AddonShotsResponse } from '@thottbot/shared';

/** `GET /api/addon/shots`, without HTTP details. */
export type PendingOutcome =
  | { kind: 'ok'; pending: AddonShotsResponse['pending'] }
  | { kind: 'unauthorized' }
  | { kind: 'error'; message: string };

/** What one `POST /api/addon/shots/:stem` came back with. */
export type ShotOutcome =
  | { kind: 'uploaded'; result: AddonShotUploadResponse }
  /** The site will never take this stem: unknown (404), removed (410) or no page for it (422). */
  | { kind: 'dropped'; code: string; message: string }
  /** This file was refused (not an image, too large); the stem may still get another file. */
  | { kind: 'rejected'; code: string; message: string }
  | { kind: 'rate_limited'; retryAfterMs: number | null }
  /** The player's Thottbot.lua is still being processed and may create the page (409). */
  | { kind: 'retry'; message: string }
  | { kind: 'unauthorized' }
  | { kind: 'error'; message: string };

export async function listPendingShots(): Promise<PendingOutcome> {
  try {
    const res = await fetch('/api/addon/shots', { credentials: 'same-origin' });
    if (res.status === 401) return { kind: 'unauthorized' };
    if (!res.ok) return { kind: 'error', message: `Could not list screenshots (${res.status})` };
    return { kind: 'ok', pending: ((await res.json()) as AddonShotsResponse).pending };
  } catch (err) {
    return { kind: 'error', message: (err as Error).message };
  }
}

export async function uploadShot(blob: Blob, stem: string, fileName: string): Promise<ShotOutcome> {
  const form = new FormData();
  form.append('file', blob, fileName);
  let res: Response;
  try {
    res = await fetch(`/api/addon/shots/${encodeURIComponent(stem)}`, {
      method: 'POST',
      body: form,
      credentials: 'same-origin',
    });
  } catch {
    return { kind: 'error', message: 'Upload failed (network error)' };
  }
  const body = (await res.json().catch(() => null)) as
    | (AddonShotUploadResponse & { error?: string; message?: string })
    | null;
  if (res.status === 200 || res.status === 201) return { kind: 'uploaded', result: body! };
  const code = body?.error ?? String(res.status);
  const message = body?.message ?? body?.error ?? `Upload failed (${res.status})`;
  if (res.status === 401) return { kind: 'unauthorized' };
  if (res.status === 429) {
    const retry = Number(res.headers.get('Retry-After'));
    return { kind: 'rate_limited', retryAfterMs: Number.isFinite(retry) && retry > 0 ? retry * 1000 : null };
  }
  if (res.status === 409) return { kind: 'retry', message };
  if (res.status === 404 || res.status === 410 || res.status === 422) return { kind: 'dropped', code, message };
  if (res.status === 400 || res.status === 413) return { kind: 'rejected', code, message };
  return { kind: 'error', message };
}
